import { Box, Link, Typography, alpha } from '@mui/material';
import { FileTextOutlined } from '@ant-design/icons';

// ==============================|| 出典アコーディオン ||============================== //
//
// 章の末尾と記事の末尾に置く、折りたたみ式の出典。
//
// MUI の Accordion ではなく素の <details> を使っている。理由は3つ。
//   1. JavaScript を使わずに開閉する。プリレンダリングしたHTMLがそのまま機能する
//   2. React の状態を持たないので、ハイドレーションのずれが起きようがない
//   3. 中身は閉じていてもHTMLに存在するため、検索エンジンは出典を読める
//
// 折りたたむ理由は「読者から見た本文の量を増やさないため」。
// 条文の引用を本文にそのまま並べると読み進める手が止まるが、
// 出典が無いと制度の記事としては信用されない。畳むことで両立させている。

const primaryColor = '#2aa1af';

type SourceItem = { label: string; quote?: string; url?: string; note?: string };

const SourceAccordion = ({ title, items, dense = false }: { title?: string; items: SourceItem[]; dense?: boolean }) => (
    <Box
        component="details"
        sx={{
            my: dense ? 0 : { xs: 3, md: 3.5 },
            borderRadius: 2,
            border: `1px solid ${alpha('#111827', 0.12)}`,
            bgcolor: '#fafafa',
            overflow: 'hidden',
            // 開いているときだけ見出しの下に区切り線を出す
            '&[open] > summary': { borderBottom: `1px solid ${alpha('#111827', 0.1)}` }
        }}
    >
        <Box
            component="summary"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: { xs: 2, md: 2.5 },
                py: 1.5,
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '0.82rem',
                fontWeight: 700,
                listStyle: 'none',
                userSelect: 'none',
                '&:hover': { color: primaryColor, bgcolor: alpha(primaryColor, 0.04) },
                // ブラウザ既定の三角マーカーを消し、自前の記号に置き換える
                '&::-webkit-details-marker': { display: 'none' },
                '&::after': {
                    content: '"＋"',
                    marginLeft: 'auto',
                    fontSize: '0.9rem',
                    lineHeight: 1,
                    color: '#9ca3af'
                },
                'details[open] > &::after': { content: '"−"' }
            }}
        >
            <Box component="span" aria-hidden="true" sx={{ display: 'flex', fontSize: '0.85rem' }}>
                <FileTextOutlined />
            </Box>
            {title ?? 'この章の根拠（出典）'}
        </Box>

        <Box component="ul" sx={{ m: 0, px: { xs: 2, md: 2.5 }, py: 2, listStyle: 'none' }}>
            {items.map((item, index) => (
                <Box component="li" key={index} sx={{ mb: index === items.length - 1 ? 0 : 2.25 }}>
                    <Typography component="p" sx={{ color: '#374151', fontSize: '0.83rem', fontWeight: 700, lineHeight: 1.75 }}>
                        {item.url ? (
                            <Link href={item.url} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: primaryColor, fontWeight: 700 }}>
                                {item.label}
                            </Link>
                        ) : (
                            item.label
                        )}
                        {item.note && (
                            <Box component="span" sx={{ color: '#9ca3af', fontWeight: 400 }}>
                                {'（'}
                                {item.note}
                                {'）'}
                            </Box>
                        )}
                    </Typography>
                    {item.quote && (
                        <Typography
                            component="blockquote"
                            sx={{
                                m: 0,
                                mt: 0.75,
                                pl: 1.75,
                                borderLeft: `3px solid ${alpha(primaryColor, 0.3)}`,
                                color: '#6b7280',
                                fontSize: '0.8rem',
                                lineHeight: 1.85
                            }}
                        >
                            {item.quote}
                        </Typography>
                    )}
                </Box>
            ))}
        </Box>
    </Box>
);

export default SourceAccordion;
